import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';
import { CreditCard, Eye, EyeOff, Save, AlertTriangle, CheckCircle, TestTube, Zap } from 'lucide-react';

const MainAdminStripeSettings = () => {
  const [settings, setSettings] = useState({
    stripe_mode: 'test',
    // Test keys
    stripe_test_publishable_key: '',
    stripe_test_secret_key: '',
    // Live keys
    stripe_live_publishable_key: '',
    stripe_live_secret_key: '',
    // Webhook secret (shared)
    stripe_webhook_secret: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTestSecretKey, setShowTestSecretKey] = useState(false);
  const [showLiveSecretKey, setShowLiveSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/main-admin/stripe-settings');
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch Stripe settings:', error);
      toast.error('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/main-admin/stripe-settings', settings);
      toast.success('Stripe settings saved successfully');
    } catch (error) {
      console.error('Failed to save Stripe settings:', error);
      toast.error('Failed to save Stripe settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const isTestMode = settings.stripe_mode === 'test';
  const hasTestKeys = settings.stripe_test_publishable_key && settings.stripe_test_secret_key;
  const hasLiveKeys = settings.stripe_live_publishable_key && settings.stripe_live_secret_key;
  
  // Check if current mode has keys configured
  const currentModeHasKeys = isTestMode ? hasTestKeys : hasLiveKeys;

  if (loading) {
    return (
      <Sidebar>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Stripe Settings</h1>
          </div>
          <p className="text-gray-600">Configure your Stripe payment integration for subscription billing.</p>
        </div>

        {/* Mode Indicator */}
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          isTestMode 
            ? 'bg-yellow-50 border border-yellow-200' 
            : 'bg-green-50 border border-green-200'
        }`}>
          <div className="flex items-center gap-3">
            {isTestMode ? (
              <>
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-800 font-medium">Test Mode - No real charges will be made</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">Live Mode - Processing real payments</span>
              </>
            )}
          </div>
          {!currentModeHasKeys && (
            <span className={`text-xs px-2 py-1 rounded ${isTestMode ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'}`}>
              Keys not configured
            </span>
          )}
        </div>

        {/* Mode Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Active Environment
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                data-testid="stripe-mode-test"
                onClick={() => handleChange('stripe_mode', 'test')}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                  isTestMode
                    ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400 shadow-sm'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                <TestTube className="w-4 h-4" />
                Test Mode
                {hasTestKeys && <CheckCircle className="w-4 h-4 ml-1 text-green-600" />}
              </button>
              <button
                type="button"
                data-testid="stripe-mode-live"
                onClick={() => handleChange('stripe_mode', 'live')}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                  !isTestMode
                    ? 'bg-green-100 text-green-800 border-2 border-green-400 shadow-sm'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                <Zap className="w-4 h-4" />
                Live Mode
                {hasLiveKeys && <CheckCircle className="w-4 h-4 ml-1 text-green-600" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Switch between test and live mode. Each mode uses its own set of API keys.
            </p>
          </div>
        </div>

        {/* Keys Configuration - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Test Keys */}
          <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
            isTestMode ? 'border-yellow-400 ring-2 ring-yellow-100' : 'border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b ${isTestMode ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <TestTube className={`w-5 h-5 ${isTestMode ? 'text-yellow-600' : 'text-gray-400'}`} />
                <h3 className={`font-semibold ${isTestMode ? 'text-yellow-800' : 'text-gray-600'}`}>
                  Test Keys
                </h3>
                {isTestMode && (
                  <span className="ml-auto text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">Active</span>
                )}
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Test Publishable Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Publishable Key
                </label>
                <input
                  type="text"
                  data-testid="stripe-test-publishable-key"
                  value={settings.stripe_test_publishable_key}
                  onChange={(e) => handleChange('stripe_test_publishable_key', e.target.value)}
                  placeholder="pk_test_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                />
              </div>
              
              {/* Test Secret Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showTestSecretKey ? 'text' : 'password'}
                    data-testid="stripe-test-secret-key"
                    value={settings.stripe_test_secret_key}
                    onChange={(e) => handleChange('stripe_test_secret_key', e.target.value)}
                    placeholder="sk_test_..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTestSecretKey(!showTestSecretKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showTestSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Live Keys */}
          <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
            !isTestMode ? 'border-green-400 ring-2 ring-green-100' : 'border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b ${!isTestMode ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${!isTestMode ? 'text-green-600' : 'text-gray-400'}`} />
                <h3 className={`font-semibold ${!isTestMode ? 'text-green-800' : 'text-gray-600'}`}>
                  Live Keys
                </h3>
                {!isTestMode && (
                  <span className="ml-auto text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Active</span>
                )}
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Live Publishable Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Publishable Key
                </label>
                <input
                  type="text"
                  data-testid="stripe-live-publishable-key"
                  value={settings.stripe_live_publishable_key}
                  onChange={(e) => handleChange('stripe_live_publishable_key', e.target.value)}
                  placeholder="pk_live_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                />
              </div>
              
              {/* Live Secret Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showLiveSecretKey ? 'text' : 'password'}
                    data-testid="stripe-live-secret-key"
                    value={settings.stripe_live_secret_key}
                    onChange={(e) => handleChange('stripe_live_secret_key', e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLiveSecretKey(!showLiveSecretKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showLiveSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-700">Webhook Configuration</h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Webhook Secret */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook Secret
              </label>
              <div className="relative">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  data-testid="stripe-webhook-secret"
                  value={settings.stripe_webhook_secret}
                  onChange={(e) => handleChange('stripe_webhook_secret', e.target.value)}
                  placeholder="whsec_..."
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Found in Stripe Dashboard → Developers → Webhooks
              </p>
            </div>

            {/* Webhook URL Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Webhook Endpoint URL</h4>
              <code className="text-sm bg-gray-200 px-3 py-1 rounded text-gray-800 break-all">
                {window.location.origin}/api/webhook/stripe
              </code>
              <p className="text-xs text-gray-500 mt-2">
                Add this URL in your Stripe Dashboard → Developers → Webhooks → Add endpoint
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-stripe-settings"
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save All Settings'}
            </button>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default MainAdminStripeSettings;
